<template>
  <div class="base-input" :class="[size, { disabled, 'has-error': error }]">
    <label v-if="label" :for="id" class="base-input__label">
      {{ label }}
      <span v-if="required" class="base-input__required">*</span>
    </label>
    
    <div class="base-input__wrapper">
      <input
        :id="id"
        ref="inputRef"
        :type="type"
        :value="formattedValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :min="min"
        :max="max"
        :step="step"
        :class="['base-input__field', inputClass]"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
        @change="handleChange"
      />
      
      <div v-if="hasSuffix" class="base-input__suffix">
        {{ suffix }}
      </div>
      
      <div v-if="clearable && modelValue !== null && modelValue !== ''" class="base-input__clear" @click="handleClear">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 6.586L11.536 3.05 12.95 4.464 9.414 8l3.536 3.536-1.414 1.414L8 9.414l-3.536 3.536-1.414-1.414L6.586 8 3.05 4.464l1.414-1.414L8 6.586z"/>
        </svg>
      </div>
    </div>
    
    <div v-if="error" class="base-input__error">
      {{ error }}
    </div>
    
    <div v-if="hint && !error" class="base-input__hint">
      {{ hint }}
    </div>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch } from 'vue'

export default {
  name: 'BaseInput',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'number', 'email', 'password', 'tel', 'url'].includes(value)
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    min: {
      type: Number,
      default: undefined
    },
    max: {
      type: Number,
      default: undefined
    },
    step: {
      type: Number,
      default: 1
    },
    suffix: {
      type: String,
      default: ''
    },
    inputClass: {
      type: String,
      default: ''
    },
    // Новая опция: автоматически применять ограничения min/max
    enforceLimits: {
      type: Boolean,
      default: true
    },
    // Новая опция: значение по умолчанию для пустого поля
    defaultValue: {
      type: Number,
      default: 0
    }
  },
  
  emits: ['update:modelValue', 'focus', 'blur', 'clear', 'keydown', 'out-of-range'],
  
  setup(props, { emit }) {
    const inputRef = ref(null)
    const isFocused = ref(false)
    const id = `input-${Math.random().toString(36).substr(2, 9)}`
    
    const hasSuffix = computed(() => props.suffix && props.suffix.length > 0)
    
    // Форматированное значение для отображения
    const formattedValue = computed(() => {
      if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') {
        return ''
      }
      return props.modelValue.toString()
    })
    
    // Проверка на выход за границы диапазона
    const isOutOfRange = (value) => {
      if (value === null || value === undefined || value === '' || isNaN(value)) return false
      
      const numValue = Number(value)
      return (
        (props.min !== undefined && numValue < props.min) ||
        (props.max !== undefined && numValue > props.max)
      )
    }
    
    // Приведение значения к допустимому диапазону
    const clampValue = (value) => {
      if (value === null || value === undefined || value === '' || isNaN(value)) {
        return props.defaultValue
      }
      
      let numValue = Number(value)
      
      if (props.min !== undefined && numValue < props.min) {
        numValue = props.min
      }
      
      if (props.max !== undefined && numValue > props.max) {
        numValue = props.max
      }
      
      return numValue
    }
    
    // Установка значения по умолчанию для пустого поля
    const setDefaultValueIfEmpty = () => {
      if (props.type === 'number' && 
          (props.modelValue === null || props.modelValue === undefined || props.modelValue === '')) {
        emit('update:modelValue', props.defaultValue)
      }
    }
    
    const handleInput = (event) => {
      let value = event.target.value
      
      if (props.type === 'number') {
        // Разрешаем пустую строку (временно)
        if (value === '') {
          emit('update:modelValue', '')
          return
        }
        
        // Проверяем, является ли значение числом
        const numValue = Number(value)
        if (isNaN(numValue)) {
          // Если не число, игнорируем ввод
          return
        }
        
        // Проверяем выход за границы
        if (isOutOfRange(numValue)) {
          emit('out-of-range', { value: numValue, min: props.min, max: props.max })
        }
        
        value = numValue
      }
      
      emit('update:modelValue', value)
    }
    
    // Обработчик изменения (срабатывает при потере фокуса)
    const handleChange = (event) => {
      if (props.type === 'number' && props.enforceLimits) {
        let value = event.target.value
        
        // Если поле пустое, устанавливаем значение по умолчанию
        if (value === '') {
          emit('update:modelValue', props.defaultValue)
          return
        }
        
        const numValue = Number(value)
        if (!isNaN(numValue)) {
          // Применяем ограничения min/max
          const clampedValue = clampValue(numValue)
          if (clampedValue !== numValue) {
            emit('update:modelValue', clampedValue)
          }
        }
      }
    }
    
    const handleFocus = (event) => {
      isFocused.value = true
      emit('focus', event)
    }
    
    const handleBlur = (event) => {
      isFocused.value = false
      
      // При потере фокуса применяем ограничения и устанавливаем значение по умолчанию для пустого поля
      if (props.type === 'number' && props.enforceLimits) {
        const value = props.modelValue
        
        // Если поле пустое, устанавливаем значение по умолчанию
        if (value === null || value === undefined || value === '') {
          emit('update:modelValue', props.defaultValue)
        } else if (!isNaN(value)) {
          // Применяем ограничения min/max
          const clampedValue = clampValue(value)
          if (clampedValue !== value) {
            emit('update:modelValue', clampedValue)
          }
        }
      }
      
      emit('blur', event)
    }
    
    const handleKeydown = (event) => {
      // Блокируем ввод нечисловых символов для числового поля
      if (props.type === 'number') {
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 
          'Tab', 'Escape', 'Enter'
        ]
        
        // Разрешаем: цифры, точки (для десятичных), минус (для отрицательных)
        const isNumber = /[0-9]/.test(event.key)
        const isDecimal = event.key === '.' && !event.target.value.includes('.')
        const isNegative = event.key === '-' && (props.min === undefined || props.min < 1)
        
        if (
          !allowedKeys.includes(event.key) &&
          !isNumber &&
          !isDecimal &&
          !isNegative &&
          !event.ctrlKey &&
          !event.metaKey
        ) {
          event.preventDefault()
        }
      }
      
      emit('keydown', event)
    }
    
    const handleClear = () => {
      // При очистке устанавливаем значение по умолчанию для числовых полей
      if (props.type === 'number') {
        emit('update:modelValue', props.defaultValue)
      } else {
        emit('update:modelValue', '')
      }
      emit('clear')
      
      nextTick(() => {
        if (inputRef.value) {
          inputRef.value.focus()
        }
      })
    }
    
    const focus = () => {
      if (inputRef.value) {
        inputRef.value.focus()
      }
    }
    
    const blur = () => {
      if (inputRef.value) {
        inputRef.value.blur()
      }
    }
    
    // Устанавливаем значение по умолчанию при монтировании, если поле пустое
    setDefaultValueIfEmpty()
    
    return {
      inputRef,
      id,
      hasSuffix,
      formattedValue,
      handleInput,
      handleFocus,
      handleBlur,
      handleKeydown,
      handleChange,
      handleClear,
      focus,
      blur
    }
  }
}
</script>

<style scoped>
/* Стили остаются без изменений */
.base-input {
  width: 100%;
}

.base-input__label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--grey-0);
  font-size: 1rem;
}

.base-input__required {
  color: #ef4444;
}

.base-input__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.base-input__field {
  width: 100%;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  border: 0rem;
  color: var(--color-text);
  transition: all 0.2s ease;
  background-color: var(--grey-1);
}

.base-input__field:focus {
  outline: none;
  border-color: var(--grey-1);
}

.base-input__field:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.base-input__field:read-only {
  background-color: #f9fafb;
}

.base-input__suffix {
  position: absolute;
  right: 0.75rem;
  color: #6b7280;
  font-size: 0.875rem;
  pointer-events: none;
}

.base-input__clear {
  position: absolute;
  right: 0.75rem;
  color: var(--grey-0);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: color 0.2s ease;
}

.base-input__clear:hover {
  color: var(--grey-0);
}

.base-input__error {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--grey-0);
}

.base-input__hint {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--grey-0);
}

/* Размеры */
.base-input.small .base-input__field {
  padding: 0.375rem 0.5rem;
  font-size: 0.875rem;
}

.base-input.large .base-input__field {
  padding: 0.75rem 1rem;
  font-size: 1.125rem;
}

/* Состояния */
.base-input.disabled .base-input__clear {
  display: none;
}

.base-input.has-error .base-input__field {
  border-color: var(--grey-0);
}

.base-input.has-error, .base-input__field:focus {
  border-color: var(--grey-0);
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
</style>